<template>
  <div
    class="relative"
    :class="[brandClasses.card, !hasBadge ? brandClasses.badgeOffset : '']"
    data-test-id="product-card"
  >
    <div class="h-full f-col">
      <product-badge
        v-if="showBadgeAboveGallery && hasBadge"
        v-bind="data.badge"
        :class="brandClasses.badge"
        data-test-id="product-card-badge"
      />
      <div class="relative w-full">
        <product-card-gallery
          :class="[brandClasses.gallery, !hasBadge ? brandClasses.galleryNoBadge : '']"
          :fetchpriority
          :items="data.gallery"
          :lazy
          :name="data.name"
          :show-secondary-media="showSecondaryMedia"
        >
          <template #badge>
            <product-badge
              v-if="!showBadgeAboveGallery && hasBadge"
              v-bind="data.badge"
              class="pointer-events-none absolute right-0"
              :class="[badgePosition === 'bottom' ? 'bottom-0' : 'top-0', brandClasses.badge]"
              data-test-id="product-card-badge"
            />
          </template>
          <template #link>
            <base-link aria-hidden="true" class="absolute-0" tabindex="-1" :to="url" @click.capture="navigateToPDP" />
          </template>
        </product-card-gallery>
        <slot name="close" v-bind="{ quickshopMode }" />
      </div>
      <!-- color swatches -->
      <template v-if="swatches.length">
        <product-swatches
          v-if="showSwatches"
          v-model="swatchModel"
          class="z-1"
          :class="brandClasses.swatches"
          :lazy
          :swatches
          @more="navigateToPDP"
        />
        <!-- show more performant text version when swatches aren't enabled -->
        <p
          v-else-if="!hideAttributes && swatches.length"
          class="text-sm underlined"
          :class="brandClasses.swatchCount"
          data-test-id="product-card-colors"
        >
          {{ showSwatchCount ? `${swatches.length} ${$pluralize($t.colors, swatches.length)}` : $t.moreColors }}
        </p>
      </template>
      <!-- eyebrow -->
      <p
        v-if="showEyebrow && data.eyebrow?.label"
        v-bind="eyebrowStyle"
        class="truncate eyebrow"
        :class="brandClasses.eyebrow"
        data-test-id="product-card-eyebrow"
      >
        {{ data.eyebrow.label }}
      </p>
      <!-- title -->
      <base-link
        :id="product.id"
        :class="[classTitle, brandClasses.title]"
        data-test-id="product-card-title"
        :to="url"
        @click.capture="navigateToPDP"
      >
        <template v-if="!$feature.enableProductCardGalleryCarousel">
          <span class="absolute-0" />
          <span
            class="absolute inset-x-0 top-0"
            :class="hasBadge ? brandClasses.badgeOffset : ''"
            :style="{ 'aspect-ratio': mediaSize.width / mediaSize.height }"
            @mouseenter="showSecondaryMedia = $viewport.lg"
            @mouseleave="showSecondaryMedia = false"
          />
        </template>
        {{ data.name }}
      </base-link>
      <!-- rating -->
      <div
        v-if="!hideRating && data.rating?.reviewsCount"
        class="flex items-center gap-1"
        :class="brandClasses.ratingWrapper"
      >
        <vf-rating data-test-id="product-card-rating" :size="ratingSize" :value="data.rating.score" />
        <span class="text-xs" :class="brandClasses.ratingReviewCount">
          ({{ data.rating.reviewsCount }})
        </span>
      </div>
      <!-- price -->
      <product-pricing
        class="mt-2 grow text-sm"
        :class="brandClasses.price"
        :currency="data.currency"
        data-test-id="product-card-pricing"
        :price="data.price"
        :show-discount-percentage="showDiscountPercentage"
      />
      <!-- quickshop -->
      <template v-if="quickshopMode !== 'off'">
        <base-button
          v-if="quickshopCta.iconOnly"
          :aria-describedby="product.id"
          :aria-expanded="opened"
          aria-haspopup="dialog"
          :aria-label="$t.add"
          :class="brandClasses.quickshopCta"
          data-test-id="product-card-add"
          @click="openQuickshop"
        >
          <vf-icon name="quickshop" size="lg" />
        </base-button>
        <vf-button
          v-else
          :aria-describedby="product.id"
          :aria-expanded="opened"
          aria-haspopup="dialog"
          class="relative mt-4"
          :class="brandClasses.quickshopCta"
          data-test-id="product-card-add"
          :size="quickshopCta.size"
          :variant="quickshopCta.variant"
          @click="openQuickshop"
        >
          <vf-icon class="mr-1" name="plus" />
          {{ $t.add }}
        </vf-button>

        <dialog-quickshop-parent v-slot="props" class-content="<lg:p-0">
          <!--
            Temporary hack to make inline quickshop work with "nested" transition
            because currently "createTemplatePromise" doesn't support dynamic transition names
           -->
          <div ref="quickshopRef" class="w-full" :class="{ 'z-3': mode === 'inline' }">
            <dialog-quickshop
              v-if="mode === 'inline'"
              v-bind="props"
              v-model:product-id="activeProduct"
              class="transition [.nested-enter-from_&,.nested-leave-to_&]:op-0"
              :mode
            />
            <dialog-quickshop v-else v-bind="props" :mode :product-id="activeProduct" />
          </div>
        </dialog-quickshop-parent>
        <slot name="compareCheckbox" v-bind="{ swatchModel }" />
      </template>
      <slot name="shopNow" v-bind="{ quickshopMode, navigateToPDP }" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Product, ProductCardExtended } from '#root/api/clients/product/data-contracts'
import type { QuickshopMode } from '#types/catalog'
import type { CSSClass } from '#types/common'
import type { ProductClickAction } from '#types/gtm'
import type { Breadcrumb, ColorOption } from '#types/product'

export type Props = {
  /**
   * Page breadcrumbs from the product list
   */
  breadcrumbs?: Breadcrumb[]
  /**
   * Allows custom title styling from the parent
   */
  classTitle?: CSSClass
  /**
   * Option to hide attributes
   */
  hideAttributes?: boolean
  /**
   * Controls lazy load property of the default product image
   */
  lazy?: boolean
  /**
   * Product data
   */
  product: Product | ProductCardExtended
  /**
   * Quickshop mode string provided by LaunchDarkly
   */
  quickshopMode?: QuickshopMode
  /**
   * Tracking category for the click and details analytics events
   */
  trackingCategory?: string
  /**
   * This prop allows showing swatches on the product card
   */
  showSwatches?: boolean
  /**
   * Sets displaying the discount amount as percentage
   */
  showDiscountPercentage?: boolean
  /**
   * The id of the selected pickup store in the filters of the parent product list, if any
   */
  storeFilterState?: string
  /**
   * This prop allows showing rating stars on the product card by LaunchDarkly
   */
  hideRating?: boolean
  /**
   * Set fetchpriority for the first product image
   */
  imageFetchPriority?: 'low' | 'high' | 'auto'
  /**
   * The index of the card in the product list, used for GTM tracking
   */
  trackingOffset?: number
}

const {
  breadcrumbs = [],
  imageFetchPriority: fetchpriority,
  trackingOffset,
  hideRating,
  product,
  quickshopMode,
  showSwatches,
  storeFilterState,
  trackingCategory
} = defineProps<Props>()

defineEmits<Emits>()

export type Emits = {
  remove: []
}

const {
  badgePosition,
  brandClasses,
  dialogOptions,
  mediaSize,
  quickshopCta,
  ratingSize,
  showBadgeAboveGallery,
  showSwatchCount
} = useAppConfig().components.product.card
const { $feature, $gtm, $viewport } = useNuxtApp()
const route = useRoute()
const router = useRouter()
const origin = useOrigin()

const DialogQuickshopParent = createDialog('quickshop', {
  position: 'bottom',
  size: ($viewport.lg ? 'lg' : undefined),
  teleport: () => !($viewport.lg && quickshopMode === 'inline'),
  type: ($viewport.lg && quickshopMode === 'inline' ? false : dialogOptions.type) as typeof dialogOptions.type
})

const quickshopRef = ref<HTMLDivElement>()
const showSecondaryMedia = ref(false)
const mode = computed(() => $viewport.lg && quickshopMode === 'inline' ? 'inline' : 'modal')
const opened = defineModel('opened', { default: false })

/*
 Some products can be marketed by Style (NF0A3XEO) or Style Color (NF0A3XEOLE4).
 When marketed by style, we need to find the default color from the color attributes.
*/
const isStyle = PRODUCT_STYLE_ID_REGEX.test(product?.id)
const productUrl = ref(product.url)
const defaultColor = (isStyle ? getDefaultColorOption(product.attributes, product.id) || {} : {}) as ColorOption
const activeProduct = ref(defaultColor.id || product.id)
const swatchId = ref(defaultColor.id || product.id)
const swatchModel = $feature.enablePlpSwatchDetails ? swatchId : activeProduct

// Fetch
const { data } = useApi().products.details(activeProduct, {}, {
  immediate: false,
  default: () => ({
    ...product,
    url: defaultColor.url || productUrl.value,
    eyebrow: formatBadgeOrEyebrow(defaultColor.eyebrow
      || product.eyebrow, product.targetPublishDate, $feature.configProductComingSoonDateFormat),
    badge: formatBadgeOrEyebrow(defaultColor.badge
      || product.badge, product.targetPublishDate, $feature.configProductComingSoonDateFormat)
  })
}) as { data: Ref<Product>, pending: Ref<boolean> }

const swatches = data.value?.attributes.find(({ type }) => type === 'color')?.options || [] as ColorOption[]

const hasBadge = computed(() => {
  const match = $feature.badgeVisibilityOnRoute.routes.some(
    (pattern) => convertPatternToRegex(pattern).test(route.path)
  )
  const isVisible = $feature.badgeVisibilityOnRoute.mode === 'show' ? match : !match
  return !!data.value.badge?.label && isVisible
})

const showEyebrow = computed(() => {
  const match = $feature.eyebrowVisibilityOnRoute.routes.some(
    (pattern) => convertPatternToRegex(pattern).test(route.path)
  )
  return $feature.eyebrowVisibilityOnRoute.mode === 'show' ? match : !match
})

const eyebrowStyle = computed(() => {
  const colorHexCode = data?.value?.eyebrow?.colorHexCode
  return colorHexCode && colorHexCode !== 'None' ? { style: `color: #${colorHexCode}` } : {}
})
const categoryURL = breadcrumbs[breadcrumbs.length - 1]?.url
const url = computed(() => {
  if (data.value?.url && storeFilterState) {
    const baseUrl = `${data.value.url.startsWith('/') ? origin : ''}${data.value.url}`
    const parsedUrl = new URL(baseUrl)

    parsedUrl.searchParams.append('storeFilter', storeFilterState)

    return `${parsedUrl.pathname}${parsedUrl.search}`
  }

  return data.value?.url
})

onClickOutside(quickshopRef, () => mode.value === 'inline' && DialogQuickshopParent.close(), {
  ignore: ['#dialogs']
})

const handleGtmEvent = (payload, action: ProductClickAction) => {
  $gtm.push('product.onProductClick', payload, {
    action,
    breadcrumbs,
    category: trackingCategory,
    offset: trackingOffset,
    searchTerm: route.query.q?.toString()
  })
}

const navigateToPDP = (e?: PointerEvent) => {
  handleGtmEvent(data.value, 'Navigate To PDP')

  const serializedBreadcrumbs = serializeAnalyticsBreadcrumbs(breadcrumbs)
  const { path, query = {} } = getRouteFromUrl(url.value)

  const state = {
    breadcrumbs: serializedBreadcrumbs,
    category: trackingCategory,
    searchTerm: route.query.q,
    categoryURL
  }

  const openInNewTab = e?.metaKey || e?.ctrlKey

  if (openInNewTab) {
    localStorage.setItem(`from_plp_${data.value.id}`, JSON.stringify(state))
  }
  else {
    e?.preventDefault()
    router.push({ path, query, state })
  }
}

const getColorSwatch = (id: string) => swatches.find((swatch) => swatch.id === id) as ColorOption

watch(swatchId, (id) => {
  if (!$feature.enablePlpSwatchDetails) return

  const colorSwatch = getColorSwatch(id)
  if (colorSwatch) {
    data.value.badge = formatBadgeOrEyebrow(
      colorSwatch.badge,
      product.targetPublishDate,
      $feature.configProductComingSoonDateFormat
    )
    data.value.price = colorSwatch.price
    data.value.eyebrow = formatBadgeOrEyebrow(
      colorSwatch.eyebrow,
      product.targetPublishDate,
      $feature.configProductComingSoonDateFormat
    )
    data.value.gallery = colorSwatch.gallery
    data.value.url = colorSwatch.url
  }
})

const openQuickshop = () => {
  if ($feature.enablePlpSwatchDetails && swatchId.value !== activeProduct.value)
    activeProduct.value = swatchId.value

  DialogQuickshopParent.open({
    product: data.value as Product,
    categoryURL,
    gtmConfig: {
      ...(trackingCategory && { category: trackingCategory }),
      ...(storeFilterState && { shippingMethod: 'BOPIS_PLP_SD', shippingStoreId: storeFilterState, shippingWindow: '24Hrs' })
    }
  })
  opened.value = true
  handleGtmEvent(data.value, 'Open Quick Shop')
}

watch(() => quickshopRef.value, () => {
  if (!quickshopRef.value) opened.value = false
})
</script>
